import React from 'react';
import { motion } from 'framer-motion';
import { Condition, ConditionStatus, ConditionCategory, ConditionPriority, ConditionSource } from '../types';
import { CircleDot, Tag, User, CalendarClock, Calendar, Mail, Globe, Send } from 'lucide-react';
import { SearchableLookup } from './SearchableLookup';
interface ConditionEditCardProps {
  condition: Condition;
  edits: Partial<Condition>;
  onChange: (id: string, field: keyof Condition, value: any) => void;
  uniqueAssignees: string[];
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
}
export function ConditionEditCard({
  condition,
  edits,
  onChange,
  uniqueAssignees,
  isSelected,
  onToggleSelect
}: ConditionEditCardProps) {
  const getValue = (field: keyof Condition) => {
    return edits[field] !== undefined ? edits[field] : condition[field];
  };
  const isChanged = (field: keyof Condition) => {
    return edits[field] !== undefined && edits[field] !== condition[field];
  };
  const handleChange = (field: keyof Condition, value: any) => {
    onChange(condition.id, field, value);
  };
  const inputClass = (changed: boolean) => `block w-full py-1.5 px-2.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444] ${changed ? 'border-[#D44444] bg-red-50 text-[#D44444] font-medium' : 'border-gray-300 bg-white text-gray-900'}`;
  const labelClass = 'flex items-center text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1';
  const renderInput = (label: string, field: keyof Condition, type: 'text' | 'date' = 'text', Icon?: React.ElementType) => <div className="flex flex-col" data-id="element-44">
      <label className={labelClass} data-id="element-45">
        {Icon && <Icon className="w-3 h-3 mr-1" data-id="element-46" />}
        {label}
      </label>
      <input type={type} value={getValue(field) as string || ''} onChange={e => handleChange(field, e.target.value)} className={inputClass(isChanged(field))} data-id="element-47" />
    </div>;
  const renderSelect = (label: string, field: keyof Condition, options: {
    label: string;
    value: string;
  }[], Icon?: React.ElementType) => <div className="flex flex-col" data-id="element-48">
      <label className={labelClass} data-id="element-49">
        {Icon && <Icon className="w-3 h-3 mr-1" data-id="element-50" />}
        {label}
      </label>
      <select value={String(getValue(field))} onChange={e => handleChange(field, e.target.value)} className={inputClass(isChanged(field))} data-id="element-51">
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-52">
            {opt.label}
          </option>)}
      </select>
    </div>;
  const renderToggle = (label: string, field: keyof Condition, Icon?: React.ElementType) => {
    const changed = isChanged(field);
    const value = getValue(field) as boolean;
    return <div className="flex items-center justify-between" data-id="element-53">
        <span className="flex items-center text-xs text-gray-600" data-id="element-54">
          {Icon && <Icon className="w-3.5 h-3.5 mr-1.5 text-gray-400" data-id="element-55" />}
          {label}
        </span>
        <button type="button" onClick={() => handleChange(field, !value)} className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:ring-offset-1 ${value ? 'bg-[#D44444]' : 'bg-gray-200'} ${changed ? 'ring-1 ring-offset-1 ring-red-300' : ''}`} data-id="element-56">
          <span className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? 'translate-x-3' : 'translate-x-0'}`} data-id="element-57" />
        </button>
      </div>;
  };
  return <motion.div layoutId={`condition-${condition.id}`} initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.98
  }} className={`bg-white rounded-lg shadow-sm border-l-[3px] border-y border-r overflow-hidden transition-colors ${isSelected ? 'border-l-[#D44444] border-y-red-200 border-r-red-200 ring-1 ring-[#D44444]' : 'border-l-[#D44444] border-gray-200'}`} data-id="element-58">
      {/* Header: Name + Description */}
      <div className={`px-5 py-3 border-b flex items-start gap-3 ${isSelected ? 'bg-red-50/50 border-red-100' : 'bg-gray-50/50 border-gray-100'}`} data-id="element-59">
        <div className="pt-1" data-id="element-60">
          <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(condition.id)} className="h-4 w-4 text-[#D44444] focus:ring-[#D44444] border-gray-300 rounded cursor-pointer" data-id="element-61" />
        </div>
        <div className="flex-1" data-id="element-62">
          <input type="text" value={getValue('name') as string} onChange={e => handleChange('name', e.target.value)} className={`text-sm font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-[#D44444] focus:outline-none px-0.5 py-0.5 w-full max-w-sm transition-colors ${isChanged('name') ? 'text-[#D44444]' : 'text-gray-900'}`} placeholder="Condition Name" data-id="element-63" />
          <textarea rows={2} value={getValue('description') as string} onChange={e => handleChange('description', e.target.value)} className={`mt-1.5 block w-full text-sm bg-transparent border border-transparent rounded hover:border-gray-300 focus:border-[#D44444] focus:ring-1 focus:ring-[#D44444] focus:outline-none px-1 py-1 resize-none transition-colors ${isChanged('description') ? 'text-[#D44444] border-[#D44444] bg-red-50' : 'text-gray-500'}`} placeholder="Description" data-id="element-64" />
        </div>
      </div>

      {/* Fields row */}
      <div className="px-5 py-3.5" data-id="element-65">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 items-end" data-id="element-66">
          {renderSelect('Status', 'status', [{
          label: 'Need',
          value: 'Need'
        }, {
          label: 'Requested',
          value: 'Requested'
        }, {
          label: 'Received',
          value: 'Received'
        }, {
          label: 'Review',
          value: 'Review'
        }, {
          label: 'Cleared',
          value: 'Cleared'
        }, {
          label: 'Waived',
          value: 'Waived'
        }, {
          label: 'N/A',
          value: 'N/A'
        }], CircleDot)}
          {renderSelect('Type', 'source', [{
          label: 'Underwriter',
          value: 'Underwriter'
        }, {
          label: 'Processor',
          value: 'Processor'
        }, {
          label: 'Loan Officer',
          value: 'Loan Officer'
        }, {
          label: 'Compliance',
          value: 'Compliance'
        }], Tag)}
          <SearchableLookup label="Assigned To" icon={User} value={getValue('assignedTo') as string} options={uniqueAssignees} onChange={val => handleChange('assignedTo', val)} placeholder="Search users..." isChanged={isChanged('assignedTo')} data-id="element-67" />
          {renderInput('ETA', 'dueDate', 'date', CalendarClock)}
          {renderInput('Requested', 'requestedDate', 'date', Calendar)}

          {/* Toggles in a compact group */}
          <div className="col-span-2 flex flex-col gap-1.5 pl-3 border-l border-gray-100" data-id="element-68">
            {renderToggle('Include In Email', 'includeInNextEmail', Mail)}
            {renderToggle('Borrower Portal', 'borrowerCanSeeInPortal', Globe)}
            {renderToggle('Sent to UW', 'sentToUnderwriter', Send)}
          </div>
        </div>
      </div>
    </motion.div>;
}